"use client"

import { create } from "zustand"

/**
 * Skip-Ads reward: watch one unskippable video ad (plus the 4 corner
 * widgets shown alongside it — see components/ads/SkipAdsModal.tsx) and get
 * a real ad-free window afterward.
 *
 * The grant is enforced centrally through useHasConsent()/hasConsentSync()
 * in components/cookie-consent.tsx — every single ad-rendering component in
 * this codebase already gates on one of those two functions as its first
 * check (that's the established, already-repurposed role of "consent" here:
 * it decided whether ads render sitewide once before, when it was changed to
 * always return true — see that file's own history). Piggybacking the grant
 * on the same choke point suppresses every placement (grid, interstitial,
 * preroll, letterbox/pillarbox, corner widgets) with one change instead of
 * touching each ad component individually and risking missing one.
 */

const SKIP_ADS_UNTIL_KEY = "goonru_skip_ads_until"
// Calendar-day string (YYYY-MM-DD, local time), not a rolling 24h timer —
// "once per day" reads as "you get one of these today," not "wait exactly
// 24h from your last click." Set the moment a grant is earned, checked
// against today's date regardless of whether that grant has since expired.
const SKIP_ADS_LAST_GRANTED_DATE_KEY = "goonru_skip_ads_last_granted_date"
// Paired with the date key above to support an admin-configurable
// max_grants_per_day (see /api/admin/skip-ads-config) instead of the old
// hardcoded "exactly once." Reset to 0 whenever the stored date isn't today.
const SKIP_ADS_GRANTS_TODAY_COUNT_KEY = "goonru_skip_ads_grants_today_count"
// Same calendar-day pattern, but for "has this browser actually been shown
// a real (organic) preroll today" — the button is only offered after that,
// so it reads as "here's a way to get ahead of the ads you were already
// going to see," not a reward dangled before anyone's seen an ad at all.
// Deliberately separate from useAdStore's hasShownFirstVideoAd, which is
// lifetime-once-ever, not per-day — that alone would make the button
// eligible forever after a single ad seen months ago, not "seen one today."
const REAL_AD_WATCHED_DATE_KEY = "goonru_real_ad_watched_date"

function todayString(): string {
  return new Date().toISOString().slice(0, 10)
}

export function isSkipAdsGrantActive(): boolean {
  try {
    const until = parseInt(localStorage.getItem(SKIP_ADS_UNTIL_KEY) ?? "0", 10)
    return Number.isFinite(until) && Date.now() < until
  } catch {
    return false
  }
}

export function hasUsedSkipAdsToday(): boolean {
  try {
    return localStorage.getItem(SKIP_ADS_LAST_GRANTED_DATE_KEY) === todayString()
  } catch {
    return false
  }
}

/** How many grants this browser has already earned today — 0 if the stored
 * date isn't today, regardless of what was last written to the count key. */
export function getGrantsUsedToday(): number {
  try {
    if (localStorage.getItem(SKIP_ADS_LAST_GRANTED_DATE_KEY) !== todayString()) return 0
    const n = parseInt(localStorage.getItem(SKIP_ADS_GRANTS_TODAY_COUNT_KEY) ?? "0", 10)
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

/** Call when a real (non-reward) video preroll actually shows — completed
 * or skipped, either way the viewer was shown one, which is all this gate
 * is checking for. */
export function markRealAdWatchedToday(): void {
  try {
    localStorage.setItem(REAL_AD_WATCHED_DATE_KEY, todayString())
  } catch {}
}

function hasWatchedRealAdToday(): boolean {
  try {
    return localStorage.getItem(REAL_AD_WATCHED_DATE_KEY) === todayString()
  } catch {
    return false
  }
}

/** Whether the reward can be earned right now — not currently in an active
 * grant, today's admin-configured allowance hasn't been spent yet (whether
 * or not the latest such grant has since expired), AND the viewer has
 * actually been shown a real preroll today. Callers use this to decide
 * whether to show the trigger button at all.
 *
 * maxPerDay comes from /api/admin/skip-ads-config (default 1, matching the
 * original hardcoded behavior) — passed in rather than fetched here so this
 * stays a synchronous, dependency-free check like the rest of this file. */
export function canEarnSkipAds(maxPerDay = 1): boolean {
  return !isSkipAdsGrantActive() && getGrantsUsedToday() < maxPerDay && hasWatchedRealAdToday()
}

export function grantSkipAds(minutes = 30) {
  try {
    const isNewDay = localStorage.getItem(SKIP_ADS_LAST_GRANTED_DATE_KEY) !== todayString()
    localStorage.setItem(SKIP_ADS_UNTIL_KEY, String(Date.now() + minutes * 60_000))
    localStorage.setItem(SKIP_ADS_LAST_GRANTED_DATE_KEY, todayString())
    const priorCount = isNewDay ? 0 : parseInt(localStorage.getItem(SKIP_ADS_GRANTS_TODAY_COUNT_KEY) ?? "0", 10)
    localStorage.setItem(SKIP_ADS_GRANTS_TODAY_COUNT_KEY, String((Number.isFinite(priorCount) ? priorCount : 0) + 1))
  } catch {}
}

/** Test-mode only: clears both the active grant and today's allowance count
 * so the button/modal can be exercised repeatedly in one session instead of
 * genuinely blocking for the rest of the day after hitting the admin cap. */
export function resetSkipAdsGrant() {
  try {
    localStorage.removeItem(SKIP_ADS_UNTIL_KEY)
    localStorage.removeItem(SKIP_ADS_LAST_GRANTED_DATE_KEY)
    localStorage.removeItem(SKIP_ADS_GRANTS_TODAY_COUNT_KEY)
  } catch {}
}

/** Milliseconds left on an active grant, 0 if none. */
export function getSkipAdsRemainingMs(): number {
  try {
    const until = parseInt(localStorage.getItem(SKIP_ADS_UNTIL_KEY) ?? "0", 10)
    return Number.isFinite(until) ? Math.max(0, until - Date.now()) : 0
  } catch {
    return 0
  }
}

// Zustand store rather than a plain module-level `let` — a raw mutable
// variable risks each importer getting its own copy of this module's state
// if the bundler ever splits SkipAdsModal.tsx and the sitewide corner
// widgets (always in the root layout) into different chunks; a shared
// store instance is the same safeguard useAdTriggerStore already relies on
// for this exact cross-component signal shape. True only while SkipAdsModal
// is actually mounted and showing its bundled ads. The 4 corner widgets are
// currently disabled sitewide (enabled: false in the frequency settings —
// no agreed home for them yet); this lets the modal force them on for the
// one moment they're actually supposed to appear, the same "escape hatch"
// shape as isAdTestModeOn() elsewhere in this codebase, without flipping
// their real sitewide setting back on.
const useSkipAdsModalStore = create<{ active: boolean; setActive: (v: boolean) => void }>((set) => ({
  active: false,
  setActive: (active) => set({ active }),
}))

export function isSkipAdsModalActive(): boolean {
  return useSkipAdsModalStore.getState().active
}
export function useSkipAdsModalActive(): boolean {
  return useSkipAdsModalStore((s) => s.active)
}
export function setSkipAdsModalActive(active: boolean) {
  useSkipAdsModalStore.getState().setActive(active)
}
